var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BroadcastToPeerMessage, LinkFileIdToSessionMessage, MeshFacet, InteractionFacet, ShowTooltipsBehavior, EnableModelTransformHandlesAction, SetSnapToGridParameterAction, SnapToGridParm, LocalTransformFacet, CollisionFacet, CollisionShapeLocalTransformFacet, CollisionMeshShapeType, DeleteModelMessage, SendMessagesAfterConfirmationAction, TooltipInfoFacet, TooltipPlacement, UpdateFacetOnModelMessage, } from "flavr-common";
import { Euler, Quaternion, Matrix4 } from "flavr-math";
import { roomService } from "@modules/room/RoomService";
import { busService } from "@modules/bus";
import { loadingSpinnerService } from "@modules/loading";
import { modelPool, useExternalMessages } from "@modules/model";
const degToRad = Math.PI / 180;
/**
 * Users can upload a mesh that will be displayed to all other users.
 * The mesh will contain some functionality when interacted with.
 */
export class UserMeshSharingService {
    constructor() {
        this.connection = busService.connection("UserMeshSharingService");
    }
    /**
     *
     * @param file
     * @param position
     * @param orientationEuler Rotation as Euler-angles using degrees.
     * @param scale A scalar Scale being applied to the mesh after loading is complete. Used when importing cad-data that is scaled to millimeters or centimeters.
     * @param scalable
     * @param outline
     * @param outlineColor
     * @param useMeshCollider
     */
    processMeshFromFile(file_1, position_1) {
        return __awaiter(this, arguments, void 0, function* (file, position, orientationEuler = [0, 0, 0], scale = 1, scalable = true, outline = true, outlineColor, useMeshCollider = true) {
            var _a;
            console.log("[ui] UserMeshSharingService: Starting to upload " + file.name + " to minio!");
            const fileType = file.name.split(".")[file.name.split(".").length - 1];
            loadingSpinnerService.toggleLoadingSpinner(true);
            const response = yield UserMeshSharingService.upload(`/api/v1/model/upload`, file);
            loadingSpinnerService.toggleLoadingSpinner(false);
            if (!response) {
                console.error("[ui] Tried to upload file to minio, but got no response.");
                return;
            }
            // Tell the server to link the file id with the session in order to delete it when session gets deleted
            const sessionName = localStorage.getItem("session");
            if (sessionName !== null)
                this.connection.send(new LinkFileIdToSessionMessage(response.file_id, sessionName));
            const minioFileId = response.file_id;
            const minioFilePath = this.getFileUrl(minioFileId, "." + fileType);
            const userName = (_a = window.localStorage.getItem("username")) !== null && _a !== void 0 ? _a : "guest";
            const date = new Date();
            const seconds = String(date.getSeconds()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            const hour = String(date.getHours()).padStart(2, "0");
            const year = `${date.getFullYear()}`;
            const month = String(date.getMonth() + 1).padStart(2, "0"); // beware: January = 0; February = 1, etc.
            const day = String(date.getDate()).padStart(2, "0");
            const fullDate = `${year}.${month}.${day}-${hour}:${minutes}:${seconds}`;
            const relativeId = `${file.name}.${userName}:${fullDate}`;
            console.log(`[ui] UserMeshSharingService: The file_id of the uploaded file is now ${minioFileId}\nThe url is ${minioFilePath}\n relativeId is ${relativeId}`);
            const currentRoom = modelPool.getRoomOrThrow(roomService.currentRoomModelUID);
            let currentChildModel = currentRoom.getChild(relativeId);
            if (currentChildModel !== undefined) {
                console.warn(`[ui] UserMeshSharingService: Tried to create a new model with name ${relativeId}, but it seems that there already is a child with that name.`);
                return;
            }
            // The model at which we attach the mesh
            const messages = [];
            currentChildModel = modelPool.createModel(relativeId, currentRoom, useExternalMessages(messages));
            const uid = currentChildModel.getUID();
            console.log(`[ui] UserMeshSharingService: Model with uid ${uid} created`);
            this.createMessageAboutTransform(messages, uid, position, orientationEuler);
            this.createMessageAboutMesh(messages, uid, minioFilePath, scale, outline, outlineColor !== null && outlineColor !== void 0 ? outlineColor : "black");
            this.createMessageAboutTooltip(messages, uid, file.name);
            this.createMessageAboutCollider(messages, uid, minioFilePath, scale, useMeshCollider);
            this.createMessageAboutInteraction(messages, uid, scale, scalable);
            messages.push(new BroadcastToPeerMessage([...messages]));
            this.connection.sendMultiple(messages);
        });
    }
    getFileUrl(fileId, fileEnding) {
        const ORIGIN = location.origin; // can be drawn out of this function once refactoring is done.
        if (fileId.startsWith("/")) {
            return `${ORIGIN}${fileId}`;
        }
        return `${ORIGIN}/api/v1/asset/${fileId}/${fileEnding}`;
    }
    /**
     * Todo: this is very similar to upload(...)in Api.tsx
     * Anybody working on this may consider either to merge it at some point or to remove the unused parameters.
     */
    static upload(endpoint, blob, onUploadProgress, signal, previewBlob, meta, fileId) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield new Promise((resolve, reject) => {
                const request = new XMLHttpRequest();
                const onAbort = () => {
                    request.abort();
                    const error = new Error("Upload aborted");
                    error.name = "AbortError";
                    error.aborted = true;
                    reject(error);
                };
                if (signal) {
                    signal.addEventListener("abort", onAbort);
                }
                request.open("post", endpoint, true);
                request.upload.addEventListener("progress", (e) => {
                    if (onUploadProgress) {
                        onUploadProgress(e.loaded / e.total);
                    }
                });
                request.addEventListener("error", (error) => {
                    if (signal) {
                        signal.removeEventListener("abort", onAbort);
                    }
                    reject(new Error(`Upload failed: ${String(error)}`));
                });
                request.addEventListener("load", () => {
                    if (signal) {
                        signal.removeEventListener("abort", onAbort);
                    }
                    if (request.status < 300) {
                        console.log("[ui] UserMeshSharingService: Upload was successful.");
                        const response = JSON.parse(request.responseText);
                        resolve(response);
                    }
                    else {
                        reject(new Error(`Upload failed ${request.statusText}`));
                    }
                });
                const formData = new FormData();
                formData.set("media", blob);
                if (previewBlob) {
                    formData.set("preview", previewBlob);
                }
                if (meta) {
                    formData.set("meta", JSON.stringify(meta));
                }
                if (fileId) {
                    formData.set("fileId", fileId);
                }
                formData.set("media", blob);
                request.send(formData);
            });
        });
    }
    createMessageAboutTooltip(messages, uid, fileName) {
        messages.push(new UpdateFacetOnModelMessage(uid, new TooltipInfoFacet(fileName + "\ndouble click for deletion", undefined, undefined, TooltipPlacement.RIGHT_OF_MODEL)));
    }
    createMessageAboutMesh(messages, uid, minioFilePath, scale, outline, color) {
        const meshFacetParams = { vertexScaleFactor: scale, applyOutline: outline, outlineColor: color };
        const meshFacet = new MeshFacet(minioFilePath, undefined, true, false, meshFacetParams);
        messages.push(new UpdateFacetOnModelMessage(uid, meshFacet));
    }
    createMessageAboutTransform(messages, uid, position, orientationEuler) {
        const transform = new LocalTransformFacet(position, this.EulerAsNumber3InDegreeToQuaternionAsNumber4(orientationEuler));
        messages.push(new UpdateFacetOnModelMessage(uid, transform));
    }
    /**
     * Note: At the moment this creates a HULL collider. This may need to be changed if
     * the CollisionModule cannot do that for more complex meshes.
     * @param messages
     * @param uid
     * @param url Same as the mesh.
     * @param scale
     * @param useMeshCollider
     */
    createMessageAboutCollider(messages, uid, url, scale, useMeshCollider = true) {
        const colliderFacet = new CollisionFacet({ shape: useMeshCollider ? CollisionMeshShapeType.MESH : CollisionMeshShapeType.HULL, meshUrl: url }, true);
        messages.push(new UpdateFacetOnModelMessage(uid, colliderFacet), new UpdateFacetOnModelMessage(uid, new CollisionShapeLocalTransformFacet(new Matrix4().makeScale(scale, scale, scale).toArray())));
    }
    /**
     * How users can interact with the mesh once it is there.
     */
    createMessageAboutInteraction(messages, uid, impliedScale, scalable = true) {
        const onClick = [
            new EnableModelTransformHandlesAction(true, impliedScale, scalable),
            new SetSnapToGridParameterAction(SnapToGridParm.SnapAngle, 0.01)
        ];
        // We don't allow deleting objects in XR mode since we only show the confirmation dialog in desktop mode, we can
        // remove this restriction once we have implemented proper dialogs in XR
        const onDoubleClick = [
            new SendMessagesAfterConfirmationAction([new DeleteModelMessage(uid), new BroadcastToPeerMessage([new DeleteModelMessage(uid)])], "Delete this item?")
        ];
        messages.push(new UpdateFacetOnModelMessage(uid, new InteractionFacet({
            behaviors: [new ShowTooltipsBehavior()],
            onClick,
            onDoubleClick
        })));
    }
    /**
     * This may not be the most efficient way to do it, but this will only be called
     * whenever the user wants to upload a mesh, so don't waste time optimizing.
     */
    EulerAsNumber3InDegreeToQuaternionAsNumber4(orientationEuler) {
        const euler = new Euler(orientationEuler[0] * degToRad, orientationEuler[1] * degToRad, orientationEuler[2] * degToRad);
        const quaternion = new Quaternion().setFromEuler(euler);
        const number4 = [0, 0, 0, 0];
        quaternion.toArray(number4);
        return number4;
    }
}
export const userMeshSharingService = new UserMeshSharingService();
